import { type Container, DisplayObject } from "pixi.js"
import { GRAPH_CONTAINER_NAME } from "@/domains/visualization/constants/graph"
import type { PositionedNode } from "@/domains/visualization/types/Graph"
import { easeInOut } from "@/domains/visualization/utils/easeInOut/easeInOut"

interface NodePosition {
  x: number
  y: number
}

type NodeChanges = {
  movedNodes: PositionedNode[]
  newNodes: PositionedNode[]
}

type AnimationState = {
  startTime: number
  startPosition: NodePosition
  targetPosition: NodePosition
  nodeInstance: DisplayObject
  isNewNode?: boolean
}

export type AnimationMap = Map<string, AnimationState>

/**
 * Initialize animation state for a node
 */
export const createAnimationState = (
  startPos: NodePosition,
  targetPos: NodePosition,
  nodeInstance: DisplayObject,
  isNewNode?: boolean
): AnimationState => ({
  startTime: performance.now(),
  startPosition: startPos,
  targetPosition: targetPos,
  nodeInstance,
  isNewNode
})

/**
 * Calculate and apply current position based on animation progress
 */
export const updateNodePosition = (animation: AnimationState, currentTime: number, duration: number): boolean => {
  const elapsed = currentTime - animation.startTime
  const progress = Math.min(elapsed / duration, 1)
  const easedProgress = easeInOut(progress)

  const x = animation.startPosition.x + (animation.targetPosition.x - animation.startPosition.x) * easedProgress
  const y = animation.startPosition.y + (animation.targetPosition.y - animation.startPosition.y) * easedProgress

  // Update the actual Pixi DisplayObject
  animation.nodeInstance.position.set(x, y)

  // Fade in new nodes
  if (animation.isNewNode) {
    animation.nodeInstance.alpha = easedProgress
  }

  return progress >= 1
}

/**
 * Detect which nodes have changed position or are new
 */
export const detectNodeChanges = (currentNodes: PositionedNode[], previousNodes: PositionedNode[]): NodeChanges => {
  const previousNodesMap = new Map(previousNodes.map((node) => [node.id, node]))

  const movedNodes: PositionedNode[] = []
  const newNodes: PositionedNode[] = []

  for (const currentNode of currentNodes) {
    const previousNode = previousNodesMap.get(currentNode.id)

    if (!previousNode) {
      newNodes.push(currentNode)
    } else if (previousNode.x !== currentNode.x || previousNode.y !== currentNode.y) {
      movedNodes.push(currentNode)
    }
  }

  return { movedNodes, newNodes }
}

/**
 * Find a node instance by ID in the stage
 */
const findNodeInstance = (stage: Container, nodeId: string): DisplayObject | null => {
  const graphContainer = stage.getChildByName(GRAPH_CONTAINER_NAME)
  return (
    graphContainer?.children?.find(
      (child): child is DisplayObject => child instanceof DisplayObject && child.name === nodeId
    ) ?? null
  )
}

/**
 * Initialize animations for nodes that have moved or are new
 */
export const initializeAnimations = (
  changes: NodeChanges,
  stage: Container,
  previousNodes: PositionedNode[],
  currentNodeInstance: DisplayObject | null
): AnimationMap => {
  const newAnimations = new Map<string, AnimationState>()

  // Initialize animations for moved nodes
  for (const movedNode of changes.movedNodes) {
    const nodeInstance = findNodeInstance(stage, movedNode.id)
    const previousNode = previousNodes.find((n) => n.id === movedNode.id)

    if (nodeInstance && previousNode) {
      newAnimations.set(
        movedNode.id,
        createAnimationState({ x: previousNode.x, y: previousNode.y }, { x: movedNode.x, y: movedNode.y }, nodeInstance)
      )
    }
  }

  // Initialize animations for new nodes
  if (currentNodeInstance && changes.newNodes.length > 0) {
    const sourcePosition = {
      x: currentNodeInstance.x,
      y: currentNodeInstance.y + currentNodeInstance.getBounds().height / 2
    }

    for (const newNode of changes.newNodes) {
      const nodeInstance = findNodeInstance(stage, newNode.id)
      if (nodeInstance) {
        nodeInstance.position.set(sourcePosition.x, sourcePosition.y)
        nodeInstance.alpha = 0

        newAnimations.set(
          newNode.id,
          createAnimationState(sourcePosition, { x: newNode.x, y: newNode.y }, nodeInstance, true)
        )
      }
    }
  }

  return newAnimations
}
